import { Noun, Verb, trackUe } from '@trello/analytics';
import { forNamespace } from 'app/common/lib/util/i18n';
const l = forNamespace('home card overflow menu title');

export type ItemKey =
  | 'subscribe'
  | 'unsubscribe'
  | 'addDueDate'
  | 'changeDueDate'
  | 'addMembers'
  | 'joinCard'
  | 'leaveCard';

export type ItemKeys = { [key in ItemKey]?: boolean };

export interface CardInfo {
  editable: boolean;
  hasDueDate: boolean;
  hasMeAsMember: boolean;
  cardId: string;
  isSubscribed: boolean;
}

export type Handlers = { [key in ItemKey]: () => void };

export interface OverflowItem {
  key: ItemKey;
  name: string;
  onClick: () => void;
  shouldShow: boolean;
}

interface TrackingOpts {
  getPrepObj: () => string;
  getIndObj: () => string;
  getMethod: (itemName: string) => string;
}

const trackSubscribe = (tracking: TrackingOpts, cardId: string) => {
  const { getPrepObj, getMethod } = tracking;

  const method = getMethod('subscribe');
  const context = { cardId };
  trackUe({
    category: Noun.HOME,
    verb: Verb.SUBSCRIBES,
    directObj: Noun.CARD,
    prepositionalObj: getPrepObj(),
    indirectObj: '',
    method,
    context,
  });
};

const trackUnsubscribe = (tracking: TrackingOpts, cardId: string) => {
  const { getPrepObj, getMethod } = tracking;

  const method = getMethod('unsubscribe');
  const context = { cardId };
  trackUe({
    category: Noun.HOME,
    verb: Verb.UNSUBSCRIBES,
    directObj: Noun.CARD,
    prepositionalObj: getPrepObj(),
    indirectObj: '',
    method,
    context,
  });
};

const trackAddDueDate = (tracking: TrackingOpts, cardId: string) => {
  const { getIndObj, getMethod } = tracking;

  const method = getMethod('due date');
  const context = { cardId };
  trackUe({
    category: Noun.HOME,
    verb: Verb.ADDS,
    directObj: Noun.DUE_DATE,
    prepositionalObj: '',
    indirectObj: getIndObj(),
    method,
    context,
  });
};

const trackChangeDueDate = (tracking: TrackingOpts, cardId: string) => {
  const { getIndObj, getMethod } = tracking;

  const method = getMethod('due date');
  const context = { cardId };
  trackUe({
    category: Noun.HOME,
    verb: Verb.CHANGES,
    directObj: Noun.DUE_DATE,
    prepositionalObj: '',
    indirectObj: getIndObj(),
    method,
    context,
  });
};

const trackAddMembers = (tracking: TrackingOpts, cardId: string) => {
  const { getIndObj, getMethod } = tracking;

  const method = getMethod('members');
  const context = { cardId };
  trackUe({
    category: Noun.HOME,
    verb: Verb.CHANGES,
    directObj: Noun.MEMBERS,
    prepositionalObj: '',
    indirectObj: getIndObj(),
    method,
    context,
  });
};

const trackJoinCard = (tracking: TrackingOpts, cardId: string) => {
  const { getIndObj, getMethod } = tracking;

  const method = getMethod('join card');
  const context = { cardId };
  trackUe({
    category: Noun.HOME,
    verb: Verb.JOINS,
    directObj: Noun.CARD,
    prepositionalObj: '',
    indirectObj: getIndObj(),
    method,
    context,
  });
};

const trackLeaveCard = (tracking: TrackingOpts, cardId: string) => {
  const { getIndObj, getMethod } = tracking;

  const method = getMethod('leave card');
  const context = { cardId };
  trackUe({
    category: Noun.HOME,
    verb: Verb.LEAVES,
    directObj: Noun.CARD,
    prepositionalObj: '',
    indirectObj: getIndObj(),
    method,
    context,
  });
};

export const getAllOverflowItems = (
  cardInfo: CardInfo,
  handlers: Handlers,
  tracking: TrackingOpts,
): OverflowItem[] => {
  const {
    isSubscribed,
    hasDueDate,
    editable,
    hasMeAsMember,
    cardId,
  } = cardInfo;

  const items: OverflowItem[] = [
    {
      key: 'subscribe',
      name: l('watch'),
      shouldShow: !isSubscribed,
      onClick: () => {
        trackSubscribe(tracking, cardId);
        handlers.subscribe();
      },
    },
    {
      key: 'unsubscribe',
      name: l('stop watching'),
      shouldShow: isSubscribed,
      onClick: () => {
        trackUnsubscribe(tracking, cardId);
        handlers.unsubscribe();
      },
    },
    {
      key: 'addDueDate',
      name: l('add due date'),
      shouldShow: !hasDueDate && editable,
      onClick: () => {
        trackAddDueDate(tracking, cardId);
        handlers.addDueDate();
      },
    },
    {
      key: 'changeDueDate',
      name: l('change due date'),
      shouldShow: hasDueDate && editable,
      onClick: () => {
        trackChangeDueDate(tracking, cardId);
        handlers.changeDueDate();
      },
    },
    {
      key: 'addMembers',
      name: l('add members to card'),
      shouldShow: editable,
      onClick: () => {
        trackAddMembers(tracking, cardId);
        handlers.addMembers();
      },
    },
    {
      key: 'joinCard',
      name: l('join card'),
      shouldShow: !hasMeAsMember && editable,
      onClick: () => {
        trackJoinCard(tracking, cardId);
        handlers.joinCard();
      },
    },
    {
      key: 'leaveCard',
      name: l('leave card'),
      shouldShow: hasMeAsMember && editable,
      onClick: () => {
        trackLeaveCard(tracking, cardId);
        handlers.leaveCard();
      },
    },
  ];

  return items;
};
